import {
  exportToExcelMultiSheet,
  importFromExcelMultiSheet,
  generateMultiSheetTemplate,
  ExcelColumn,
} from "./excelUtils";

// Interface for preliminary items records (matching PreliminaryItemRecord in component)
export interface PreliminaryItemRecord {
  id: string;
  description: string;
  quantity: number;
  unit: string;
  rate: number;
  amount: number;
}

export interface PreliminaryItemsSimpleExcelData {
  preliminaryItems: PreliminaryItemRecord[];
}

// Column definitions for preliminary items
const preliminaryItemColumns: ExcelColumn[] = [
  { key: "description", title: "Items" },
  {
    key: "quantity",
    title: "Quantity",
    render: (value) => Number(value || 0).toFixed(2),
  },
  { key: "unit", title: "Unit" },
  {
    key: "rate",
    title: "Rate (£)",
    render: (value) => Number(value || 0).toFixed(2),
  },
  {
    key: "amount",
    title: "Amount (£)",
    render: (value) => Number(value || 0).toFixed(2),
  },
];

// Export preliminary items data to Excel
export const exportPreliminaryItemsSimpleToExcel = async (
  data: PreliminaryItemsSimpleExcelData,
  projectName: string
): Promise<void> => {
  const sheets = [
    {
      name: "Preliminary Items",
      data: data.preliminaryItems,
      columns: preliminaryItemColumns,
    },
  ];

  await exportToExcelMultiSheet({
    filename: `${projectName}_PreliminaryItems_Export`,
    includeBranding: true,
    projectName: projectName,
    sheets: sheets,
  });
};

// Import preliminary items data from Excel
export const importPreliminaryItemsSimpleFromExcel = async (
  file: File
): Promise<{
  data: PreliminaryItemsSimpleExcelData;
  errors: string[];
  warnings: string[];
}> => {
  const options = {
    expectedSheets: [
      {
        name: "Preliminary Items",
        expectedColumns: preliminaryItemColumns.map((col) => col.title),
      },
    ],
    preserveEmptyValues: true,
  };
  const result = await importFromExcelMultiSheet(file, options);

  const data: PreliminaryItemsSimpleExcelData = {
    preliminaryItems: [],
  };

  const errors: string[] = [];
  const warnings: string[] = [];

  // Add global errors
  errors.push(...result.globalErrors);

  console.log(
    "Import result sheets:",
    result.sheets.map((s) => ({ name: s.name, dataLength: s.data.length }))
  );

  // Process each sheet
  result.sheets.forEach((sheet) => {
    try {
      console.log(
        `Processing sheet: ${sheet.name} with ${sheet.data.length} rows`
      );

      switch (sheet.name) {
        case "Preliminary Items":
          if (sheet.data.length > 0) {
            data.preliminaryItems = transformPreliminaryItemData(sheet.data);
            console.log(
              `Imported ${data.preliminaryItems.length} preliminary items`
            );
          }
          break;
        default:
          warnings.push(`Unknown sheet: ${sheet.name}`);
      }

      // Add sheet-specific errors and warnings
      errors.push(...sheet.errors);
      warnings.push(...sheet.warnings);
    } catch (error) {
      errors.push(`Error processing sheet ${sheet.name}: ${error.message}`);
    }
  });

  return {
    data,
    errors,
    warnings,
  };
};

// Transform functions for preliminary items data
const transformPreliminaryItemData = (
  rawData: any[]
): PreliminaryItemRecord[] => {
  return rawData.map((row: any, index: number) => {
    const quantity = Number(row["Quantity"] || row.quantity || 0);
    const rate = Number(row["Rate (£)"] || row.rate || 0);
    const importedAmount = Number(row["Amount (£)"] || row.amount || 0);

    // Auto-calculate amount based on quantity * rate
    const calculatedAmount = quantity * rate;

    // Use calculated amount (always recalculate for consistency)
    const finalAmount = calculatedAmount;

    return {
      id: row.id || `imported_pi_${Date.now()}_${index}`,
      description: String(row["Items"] || row.description || ""),
      quantity: quantity,
      unit: String(row["Unit"] || row.unit || ""),
      rate: rate,
      amount: finalAmount,
    };
  });
};

// Generate preliminary items template
export const generatePreliminaryItemsSimpleTemplate = async (
  projectName: string
): Promise<void> => {
  const sheets = [
    {
      name: "Preliminary Items",
      columns: preliminaryItemColumns,
    },
  ];

  await generateMultiSheetTemplate(
    `${projectName}_PreliminaryItems_Template`,
    sheets
  );
};

// Validate preliminary items data
export const validatePreliminaryItemsSimpleData = (
  data: PreliminaryItemsSimpleExcelData
): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  // Validate preliminary items
  data.preliminaryItems?.forEach((item, index) => {
    if (!item.description) {
      errors.push(`Preliminary Item ${index + 1}: Description is required`);
    }
    if (item.quantity < 0) {
      errors.push(`Preliminary Item ${index + 1}: Quantity cannot be negative`);
    }
    if (item.rate < 0) {
      errors.push(`Preliminary Item ${index + 1}: Rate cannot be negative`);
    }

    // Validate that amount matches calculated value (quantity * rate)
    const expectedAmount = item.quantity * item.rate;
    if (Math.abs(item.amount - expectedAmount) > 0.01) {
      // This is just a warning, not an error, as we auto-correct it
      console.warn(
        `Preliminary Item ${
          index + 1
        }: Amount was auto-corrected from imported value to calculated value (${
          item.quantity
        } × ${item.rate} = ${expectedAmount})`
      );
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
  };
};
